import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import {
  DollarSign, ArrowLeft, Loader2, Check, X, AlertTriangle,
  Calendar, Target, Tag, Megaphone, Globe, Eye, Users, BarChart3,
  MousePointer, ShoppingCart, Smartphone, Image, Film, Layers,
  ChevronDown, Sparkles, Wand2, TrendingUp,
} from "lucide-react";

const PLATFORMS = [
  { value: "facebook", label: "Facebook / Meta", color: "bg-blue-600" },
  { value: "instagram", label: "Instagram", color: "bg-gradient-to-r from-purple-500 to-pink-500" },
  { value: "google", label: "Google Ads", color: "bg-blue-500" },
  { value: "linkedin", label: "LinkedIn", color: "bg-blue-700" },
  { value: "tiktok", label: "TikTok", color: "bg-black" },
  { value: "twitter", label: "X (Twitter)", color: "bg-black" },
];

const OBJECTIVES = [
  { value: "awareness", label: "Brand Awareness", icon: Eye, desc: "Maximize reach & impressions" },
  { value: "traffic", label: "Website Traffic", icon: MousePointer, desc: "Drive clicks to your site" },
  { value: "engagement", label: "Engagement", icon: Users, desc: "Get likes, comments, shares" },
  { value: "leads", label: "Lead Generation", icon: Target, desc: "Capture contact information" },
  { value: "conversions", label: "Conversions", icon: ShoppingCart, desc: "Drive purchases or signups" },
  { value: "app_installs", label: "App Installs", icon: Smartphone, desc: "Promote app downloads" },
];

const AD_FORMATS = [
  { value: "single_image", label: "Single Image", icon: Image },
  { value: "carousel", label: "Carousel", icon: Layers },
  { value: "video", label: "Video", icon: Film },
  { value: "stories", label: "Stories / Reels", icon: Smartphone },
];

const BID_STRATEGIES = [
  { value: "auto", label: "Automatic (Recommended)", desc: "Platform optimizes your bids" },
  { value: "manual_cpc", label: "Manual CPC", desc: "Set cost per click" },
  { value: "manual_cpm", label: "Manual CPM", desc: "Set cost per 1000 impressions" },
  { value: "target_cpa", label: "Target CPA", desc: "Set target cost per acquisition" },
  { value: "target_roas", label: "Target ROAS", desc: "Set target return on ad spend" },
];

function Toast({ message, type, onClose }: { message: string; type: "success" | "error"; onClose: () => void }) {
  useEffect(() => { const t = setTimeout(onClose, 4000); return () => clearTimeout(t); }, [onClose]);
  return (
    <div className={`fixed bottom-6 right-6 z-50 flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg text-white text-sm font-medium ${type === "success" ? "bg-green-600" : "bg-red-600"}`}>
      {type === "success" ? <Check className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
      {message}
      <button onClick={onClose} className="ml-2 hover:opacity-70"><X className="w-3.5 h-3.5" /></button>
    </div>
  );
}

export default function CreateAd() {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{message: string; type: "success"|"error"} | null>(null);
  const [campaigns, setCampaigns] = useState<any[]>([]);
  const [audiences, setAudiences] = useState<any[]>([]);
  const [step, setStep] = useState(1);

  // Form
  const [name, setName] = useState("");
  const [platform, setPlatform] = useState("facebook");
  const [campaignId, setCampaignId] = useState("");
  const [objective, setObjective] = useState("awareness");
  const [adFormat, setAdFormat] = useState("single_image");
  const [dailyBudget, setDailyBudget] = useState("");
  const [totalBudget, setTotalBudget] = useState("");
  const [bidStrategy, setBidStrategy] = useState("auto");
  const [bidAmount, setBidAmount] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [audienceId, setAudienceId] = useState("");
  const [notes, setNotes] = useState("");
  const [tags, setTags] = useState("");

  // Targeting
  const [ageMin, setAgeMin] = useState("18");
  const [ageMax, setAgeMax] = useState("65");
  const [genders, setGenders] = useState<string[]>(["all"]);
  const [locations, setLocations] = useState("United States");
  const [interests, setInterests] = useState("");

  // AI
  const [showAI, setShowAI] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    api.get<any>("/campaigns?limit=100").then(d => setCampaigns(d.campaigns || [])).catch(() => {});
    api.get<any>("/ads/audiences").then(d => setAudiences(d || [])).catch(() => {});
  }, []);

  const handleAiGenerate = async () => {
    if (!aiPrompt.trim()) return;
    setGenerating(true);
    try {
      const data = await api.post<any>("/ai/generate/ad", { prompt: aiPrompt, platform, objective });
      if (data.text) setNotes(data.text);
      if (data.headline) setName(prev => prev || data.headline);
      setToast({ message: "AI ad copy generated!", type: "success" });
    } catch {
      setToast({ message: "AI generation failed", type: "error" });
    }
    setGenerating(false);
  };

  const handleSave = async () => {
    if (!name.trim()) { setToast({ message: "Ad name is required", type: "error" }); return; }
    setSaving(true);
    try {
      const targeting = {
        ageRange: { min: parseInt(ageMin), max: parseInt(ageMax) },
        genders, locations: locations.split(",").map(l => l.trim()),
        interests: interests ? interests.split(",").map(i => i.trim()) : [],
      };
      const payload: any = {
        name: name.trim(), platform, objective, adFormat, bidStrategy,
        dailyBudget: dailyBudget ? parseFloat(dailyBudget) : 0,
        totalBudget: totalBudget ? parseFloat(totalBudget) : 0,
        bidAmount: bidAmount ? parseFloat(bidAmount) : undefined,
        campaignId: campaignId || undefined,
        audienceId: audienceId || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        notes: notes || undefined,
        tags: tags || undefined,
        targeting,
      };
      await api.post("/ads", payload);
      setToast({ message: "Ad campaign created!", type: "success" });
      setTimeout(() => navigate("/ads"), 1200);
    } catch (err: any) {
      setToast({ message: err.message || "Failed to create ad", type: "error" });
    }
    setSaving(false);
  };

  const estimatedReach = dailyBudget ? Math.round(parseFloat(dailyBudget) * 280) : 0;
  const estimatedClicks = dailyBudget ? Math.round(parseFloat(dailyBudget) * 12) : 0;

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate("/ads")} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <ArrowLeft className="w-5 h-5 text-gray-500" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Create Ad Campaign</h1>
            <p className="text-sm text-gray-500 mt-0.5">Set up a new paid advertising campaign</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/ads")}
            className="flex items-center gap-2 px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving}
            className="btn-primary text-sm flex items-center gap-2">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            Launch Ad
          </button>
        </div>
      </div>

      {/* Step Indicator */}
      <div className="flex items-center gap-2">
        {[{ n: 1, l: "Setup" }, { n: 2, l: "Targeting" }, { n: 3, l: "Budget" }].map(s => (
          <button key={s.n} onClick={() => setStep(s.n)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              step === s.n ? "bg-purple-600 text-white shadow-sm" : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-50"
            }`}>
            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
              step === s.n ? "bg-white/20" : "bg-gray-100"
            }`}>{s.n}</span>
            {s.l}
          </button>
        ))}
      </div>

      {/* Step 1: Setup */}
      {step === 1 && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">Ad Name <span className="text-red-500">*</span></label>
                <input type="text" value={name} onChange={e => setName(e.target.value)}
                  placeholder="e.g., Spring Sale - Image Ad - US"
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">Notes / Ad Copy</label>
                <textarea value={notes} onChange={e => setNotes(e.target.value)}
                  placeholder="Primary text, headline, and description for your ad..."
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none resize-none" />
              </div>
            </div>

            {/* Objective */}
            <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
              <label className="block text-sm font-semibold text-gray-900">Campaign Objective</label>
              <div className="grid grid-cols-2 gap-3">
                {OBJECTIVES.map(o => (
                  <button key={o.value} onClick={() => setObjective(o.value)}
                    className={`flex items-start gap-3 p-4 rounded-xl text-left transition-all border ${
                      objective === o.value ? "ring-2 ring-purple-500 border-purple-200 bg-purple-50/30" : "border-gray-100 hover:border-gray-200"
                    }`}>
                    <o.icon className={`w-5 h-5 mt-0.5 ${objective === o.value ? "text-purple-600" : "text-gray-400"}`} />
                    <div>
                      <p className={`text-sm font-medium ${objective === o.value ? "text-purple-700" : "text-gray-900"}`}>{o.label}</p>
                      <p className="text-xs text-gray-400 mt-0.5">{o.desc}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* AI */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <button onClick={() => setShowAI(!showAI)}
                className="w-full flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-gradient-to-r from-violet-500 to-purple-500 flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-white" />
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-semibold text-gray-900">AI Ad Copy Generator</p>
                    <p className="text-xs text-gray-500">Generate high-converting ad copy</p>
                  </div>
                </div>
                <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showAI ? "rotate-180" : ""}`} />
              </button>
              {showAI && (
                <div className="px-6 pb-6 space-y-3">
                  <textarea value={aiPrompt} onChange={e => setAiPrompt(e.target.value)}
                    placeholder="Describe your product/service and target audience..."
                    rows={3} className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm outline-none resize-none" />
                  <button onClick={handleAiGenerate} disabled={generating || !aiPrompt.trim()}
                    className="btn-primary text-sm flex items-center gap-2 disabled:opacity-50">
                    {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
                    {generating ? "Generating..." : "Generate Ad Copy"}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-3">
              <label className="block text-sm font-semibold text-gray-900">Platform</label>
              <div className="space-y-2">
                {PLATFORMS.map(p => (
                  <button key={p.value} onClick={() => setPlatform(p.value)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${
                      platform === p.value ? "ring-2 ring-purple-500 bg-purple-50/50" : "hover:bg-gray-50 border border-gray-100"
                    }`}>
                    <div className={`w-3 h-3 rounded-full ${p.color}`} />
                    <span className={platform === p.value ? "font-semibold text-purple-700" : "text-gray-700"}>{p.label}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-3">
              <label className="block text-sm font-semibold text-gray-900">Ad Format</label>
              <div className="grid grid-cols-2 gap-2">
                {AD_FORMATS.map(f => (
                  <button key={f.value} onClick={() => setAdFormat(f.value)}
                    className={`flex flex-col items-center gap-1.5 p-3 rounded-lg text-xs transition-all ${
                      adFormat === f.value ? "ring-2 ring-purple-500 bg-purple-50 text-purple-700" : "border border-gray-100 hover:bg-gray-50 text-gray-600"
                    }`}>
                    <f.icon className="w-5 h-5" />
                    {f.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-3">
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                <Megaphone className="w-4 h-4 text-blue-600" /> Link to Campaign
              </label>
              <select value={campaignId} onChange={e => setCampaignId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-purple-500 outline-none">
                <option value="">No parent campaign</option>
                {campaigns.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Step 2: Targeting */}
      {step === 2 && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-5">
              <h3 className="text-sm font-semibold text-gray-900">Audience Targeting</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Min Age</label>
                  <input type="number" value={ageMin} onChange={e => setAgeMin(e.target.value)} min="13" max="65"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm outline-none" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Max Age</label>
                  <input type="number" value={ageMax} onChange={e => setAgeMax(e.target.value)} min="18" max="65"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm outline-none" />
                </div>
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Gender</label>
                <div className="flex gap-2">
                  {["all", "male", "female"].map(g => (
                    <button key={g} onClick={() => setGenders([g])}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                        genders.includes(g) ? "bg-purple-600 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                      }`}>
                      {g === "all" ? "All" : g === "male" ? "Male" : "Female"}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Locations</label>
                <input type="text" value={locations} onChange={e => setLocations(e.target.value)}
                  placeholder="United States, Canada, UK"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm outline-none" />
                <p className="text-xs text-gray-400 mt-1">Comma separated locations</p>
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Interests</label>
                <input type="text" value={interests} onChange={e => setInterests(e.target.value)}
                  placeholder="Technology, Cloud Computing, SaaS"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm outline-none" />
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-3">
              <label className="block text-sm font-semibold text-gray-900">Saved Audiences</label>
              <select value={audienceId} onChange={e => setAudienceId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white outline-none">
                <option value="">Custom targeting (above)</option>
                {audiences.map((a: any) => <option key={a.id} value={a.id}>{a.name} ({a.estimatedSize?.toLocaleString()} people)</option>)}
              </select>
            </div>
          </div>

          <div className="space-y-6">
            <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl border border-blue-100 p-5 space-y-3">
              <h4 className="text-sm font-semibold text-blue-900 flex items-center gap-2">
                <Users className="w-4 h-4" /> Estimated Reach
              </h4>
              <p className="text-3xl font-bold text-blue-700">{estimatedReach.toLocaleString()}</p>
              <p className="text-xs text-blue-600">people/day based on budget</p>
              <div className="pt-2 border-t border-blue-100 space-y-1">
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">Est. Clicks/day</span>
                  <span className="font-medium">{estimatedClicks}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">Ages</span>
                  <span className="font-medium">{ageMin}-{ageMax}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">Gender</span>
                  <span className="font-medium capitalize">{genders[0]}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Step 3: Budget */}
      {step === 3 && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-5">
              <h3 className="text-sm font-semibold text-gray-900">Budget & Schedule</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Daily Budget</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                    <input type="number" value={dailyBudget} onChange={e => setDailyBudget(e.target.value)}
                      placeholder="50.00" min="0" step="5"
                      className="w-full pl-7 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm outline-none" />
                  </div>
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Total Budget</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                    <input type="number" value={totalBudget} onChange={e => setTotalBudget(e.target.value)}
                      placeholder="1,000.00" min="0" step="100"
                      className="w-full pl-7 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm outline-none" />
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Start Date</label>
                  <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm outline-none" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">End Date</label>
                  <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm outline-none" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
              <label className="block text-sm font-semibold text-gray-900">Bid Strategy</label>
              <div className="space-y-2">
                {BID_STRATEGIES.map(b => (
                  <button key={b.value} onClick={() => setBidStrategy(b.value)}
                    className={`w-full flex items-center justify-between p-3 rounded-lg text-left transition-all border ${
                      bidStrategy === b.value ? "ring-2 ring-purple-500 border-purple-200 bg-purple-50/30" : "border-gray-100 hover:border-gray-200"
                    }`}>
                    <div>
                      <p className={`text-sm font-medium ${bidStrategy === b.value ? "text-purple-700" : "text-gray-900"}`}>{b.label}</p>
                      <p className="text-xs text-gray-400">{b.desc}</p>
                    </div>
                    {bidStrategy === b.value && <Check className="w-5 h-5 text-purple-600" />}
                  </button>
                ))}
              </div>
              {bidStrategy !== "auto" && (
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Bid Amount</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                    <input type="number" value={bidAmount} onChange={e => setBidAmount(e.target.value)}
                      placeholder="2.50" min="0" step="0.10"
                      className="w-full pl-7 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm outline-none" />
                  </div>
                </div>
              )}
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-3">
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                <Tag className="w-4 h-4 text-amber-600" /> Tags
              </label>
              <input type="text" value={tags} onChange={e => setTags(e.target.value)}
                placeholder="spring-sale, retargeting, lookalike"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm outline-none" />
            </div>
          </div>

          <div className="space-y-6">
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border border-green-100 p-5 space-y-3">
              <h4 className="text-sm font-semibold text-green-900 flex items-center gap-2">
                <TrendingUp className="w-4 h-4" /> Budget Summary
              </h4>
              <div className="space-y-2 text-xs">
                <div className="flex justify-between">
                  <span className="text-gray-500">Daily Budget</span>
                  <span className="font-bold text-green-700">{dailyBudget ? `$${parseFloat(dailyBudget).toLocaleString()}` : "—"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Total Budget</span>
                  <span className="font-bold text-green-700">{totalBudget ? `$${parseFloat(totalBudget).toLocaleString()}` : "—"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Duration</span>
                  <span className="font-medium">{startDate && endDate ? `${Math.ceil((new Date(endDate).getTime() - new Date(startDate).getTime()) / 86400000)} days` : "—"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Bid Strategy</span>
                  <span className="font-medium capitalize">{bidStrategy.replace("_", " ")}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Est. Daily Reach</span>
                  <span className="font-medium">{estimatedReach.toLocaleString()}</span>
                </div>
              </div>
            </div>

            <button onClick={handleSave} disabled={saving}
              className="w-full btn-primary text-sm flex items-center justify-center gap-2 py-3">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              Launch Ad Campaign
            </button>
          </div>
        </div>
      )}

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}
